<template>
  <div class="analysis-status-card h-full flex flex-col">
    <!-- 整体进度条 -->
    <div class="mb-2 flex items-center justify-between">
      <span class="text-sm font-medium text-gray-700">整体进度</span>
      <span class="text-sm font-medium transition-colors duration-300" :class="getProgressColor()">
        {{ progress }}%
      </span>
    </div>

    <div class="h-3 w-full rounded-full bg-gray-200">
      <div
        class="h-3 rounded-full transition-all duration-500 ease-out"
        :class="getProgressBarClass()"
        :style="{ width: `${progress}%` }"
      ></div>
    </div>

    <div class="mt-2">
      <div class="flex items-center space-x-1">
        <span
          class="flex-1 text-xs transition-colors duration-300"
          :class="getMessageColor()"
          :title="isError && props.error && props.error.length > 80 ? props.error : ''"
        >
          <span class="line-clamp-2">{{ displayMessage }}</span>
        </span>
        <a-tooltip v-if="isError && props.error && props.error.length > 80" :title="props.error">
          <icon-vue
            icon="mdi:information-outline"
            class="flex-shrink-0 cursor-help text-gray-400 hover:text-gray-600"
            font-size="14px"
          />
        </a-tooltip>
      </div>
    </div>

    <!-- AI思考过程预览 -->
    <div class="flex flex-col border-t pt-4">
      <div class="mb-3 flex items-center justify-between">
        <h3 class="flex items-center text-sm font-medium text-gray-800">
          <icon-vue class="mr-1 text-purple-600" icon="mdi:brain" font-size="18px" />
          <span>AI思考过程</span>
        </h3>
        <div class="flex items-center space-x-2">
          <span class="h-2 w-2 rounded-full transition-colors duration-300" :class="getThinkingIndicatorClass()"></span>
          <span v-if="tokenStats && isCompleted" class="rounded bg-green-100 px-2 py-1 text-xs text-green-700">
            {{ tokenStats }} Tokens
          </span>
          <a-button
            type="text"
            size="small"
            :icon="h(FullscreenOutlined)"
            title="查看完整思考过程"
            @click="emit('viewFullThinking')"
          />
        </div>
      </div>

      <div class="thinking-preview overflow-hidden" :class="getThinkingPreviewClass()" style="height: 300px">
        <!-- 错误状态优先显示 -->
        <div v-if="isError" class="error-state">
          <div class="py-8 text-center">
            <div class="relative mb-4">
              <div
                class="relative mx-auto h-12 w-12 flex items-center justify-center rounded-full from-red-500 to-pink-600 bg-gradient-to-r shadow-lg"
              >
                <icon-vue class="text-white" icon="mdi:brain" font-size="24px" />
              </div>
            </div>
            <h4 class="mb-2 text-sm font-medium text-gray-700">AI分析失败</h4>
            <p class="mb-1 text-xs font-medium text-red-600">{{ getErrorTitle() }}</p>
            <p class="mb-3 text-xs text-gray-500">点击下方按钮重新分析</p>
            <a-button type="primary" danger size="small" @click="emit('retryAnalysis')">
              <template #icon>
                <reload-outlined />
              </template>
              重新分析
            </a-button>
          </div>
        </div>

        <!-- 空状态 - 没有思考内容且不是错误状态 -->
        <div v-else-if="!hasThinkingContent" class="empty-state">
          <div class="py-8 text-center">
            <div class="relative mb-4">
              <div
                class="relative mx-auto h-12 w-12 flex items-center justify-center rounded-full from-purple-500 to-blue-600 bg-gradient-to-r shadow-lg"
              >
                <icon-vue class="text-white" icon="mdi:brain" font-size="24px" />
                <!-- 思考波纹效果 -->
                <div
                  v-if="isAnalyzing"
                  class="absolute inset-0 animate-ping border-2 border-purple-300 rounded-full opacity-60"
                  style="animation-duration: 3s"
                ></div>
                <div
                  v-if="isAnalyzing"
                  class="absolute inset-0 animate-ping border-2 border-purple-300 rounded-full opacity-40"
                  style="animation-delay: 1s; animation-duration: 3s"
                ></div>
              </div>
            </div>
            <h4 class="mb-2 text-sm font-medium text-gray-700">AI智能分析引擎</h4>
            <p class="text-xs leading-relaxed text-gray-500">
              AI将深度分析代码结构，
              <br />
              生成
              <span class="font-medium text-purple-600">智能测试策略</span>
            </p>
            <div class="mt-4 flex justify-center">
              <div class="flex items-center text-xs text-gray-400 space-x-2">
                <span class="h-2 w-2 rounded-full bg-purple-500" :class="{ 'animate-pulse': isAnalyzing }"></span>
                <span>{{ isAnalyzing ? '正在分析中' : '等待启动分析' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 有思考内容时显示 -->
        <div v-else ref="thinkingContentRef" class="thinking-content h-full overflow-y-auto" @scroll="handleScroll">
          <div class="p-3">
            <div class="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-600">
              {{ displayThinkingContent }}
            </div>

            <!-- 滚动提示 -->
            <div v-if="isUserScrolling && isAnalyzing" class="sticky bottom-2 left-0 right-0 mt-2 flex justify-center">
              <div class="scroll-to-bottom-btn" @click="clickScrollToBottom">
                <icon-vue icon="mdi:arrow-down" font-size="14px" />
                <span>新内容正在生成，点击跳转到底部</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AnalysisStage, AnalysisStatus } from '@/types/precision-new/ai-analysis';
import { FullscreenOutlined, ReloadOutlined } from '@ant-design/icons-vue';
import { Icon as IconVue } from '@iconify/vue';
import { computed, h, nextTick, ref, watch } from 'vue';

// Props
interface Props {
  status: AnalysisStatus;
  progress: number;
  stage: AnalysisStage;
  message?: string;
  error?: string;
  thinkingContent?: string;
  tokenStats?: string;
}

const props = withDefaults(defineProps<Props>(), {
  status: 'idle',
  progress: 0,
  stage: 'data_collection',
  message: '',
  error: '',
  thinkingContent: '',
  tokenStats: '',
});

// Emits
const emit = defineEmits<{
  viewFullThinking: [];
  retryAnalysis: [];
}>();

// 计算属性
const isAnalyzing = computed(() => props.status === 'analyzing');
const isCompleted = computed(() => props.status === 'completed');
const isError = computed(() => props.status === 'error');
const hasThinkingContent = computed(() => !!props.thinkingContent);

const displayMessage = computed(() => {
  if (isError.value) {
    const errorMsg = props.error || '分析失败';
    // 如果错误信息过长，截断显示
    if (errorMsg.length > 80) {
      return errorMsg.substring(0, 80) + '...';
    }
    return errorMsg;
  }
  if (isCompleted.value) {
    return '分析完成';
  }
  return props.message || '等待开始分析...';
});

const displayThinkingContent = computed(() => {
  if (!props.thinkingContent) {
    return '';
  }

  // 在分析状态卡片中显示完整内容，支持SSE流式输出
  return props.thinkingContent;
});

// 思考内容容器引用
const thinkingContentRef = ref<HTMLElement>();

// 滚动控制状态
const isUserScrolling = ref(false);
const autoScrollEnabled = ref(true);

// 样式计算
const getProgressColor = () => {
  if (isError.value) {
    return 'text-red-600';
  }
  if (isCompleted.value) {
    return 'text-green-600';
  }
  if (isAnalyzing.value) {
    return 'text-blue-600';
  }
  return 'text-gray-600';
};

const getProgressBarClass = () => {
  if (isError.value) {
    return 'bg-gradient-to-r from-red-500 to-red-600';
  }
  if (isCompleted.value) {
    return 'bg-gradient-to-r from-green-500 to-green-600';
  }
  return 'bg-gradient-to-r from-blue-500 to-purple-600';
};

const getMessageColor = () => {
  if (isError.value) {
    return 'text-red-600';
  }
  if (isCompleted.value) {
    return 'text-green-600';
  }
  return 'text-gray-500';
};

const getThinkingIndicatorClass = () => {
  if (isError.value) {
    return 'bg-red-500';
  }
  if (isCompleted.value) {
    return 'bg-green-500';
  }
  if (isAnalyzing.value) {
    return 'bg-purple-500 animate-pulse';
  }
  return 'bg-gray-400';
};

const getThinkingPreviewClass = () => {
  const baseClass = 'rounded-lg border';

  if (isError.value) {
    return `${baseClass} border-red-200 bg-red-50`;
  }
  if (isCompleted.value) {
    return `${baseClass} border-green-200 bg-green-50`;
  }
  return `${baseClass} border-gray-200 bg-gray-50`;
};

// 滚动控制函数
const scrollToBottom = () => {
  if (thinkingContentRef.value && autoScrollEnabled.value) {
    nextTick(() => {
      thinkingContentRef.value!.scrollTop = thinkingContentRef.value!.scrollHeight;
    });
  }
};

// 点击跳转到底部
const clickScrollToBottom = () => {
  if (thinkingContentRef.value && !autoScrollEnabled.value) {
    nextTick(() => {
      thinkingContentRef.value!.scrollTop = thinkingContentRef.value!.scrollHeight;
    });
  }
};

// 处理用户滚动
const handleScroll = () => {
  if (!thinkingContentRef.value) {
    return;
  }

  const { scrollTop, scrollHeight, clientHeight } = thinkingContentRef.value;
  const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px容差

  // 如果用户滚动到底部，重新启用自动滚动
  if (isAtBottom) {
    autoScrollEnabled.value = true;
    isUserScrolling.value = false;
  } else {
    // 用户手动滚动到其他位置，禁用自动滚动
    autoScrollEnabled.value = false;
    isUserScrolling.value = true;
  }
};

// 监听思考内容变化，实现SSE流式输出效果
watch(
  () => props.thinkingContent,
  (newContent, oldContent) => {
    // 只有在分析中且内容增加时才自动滚动
    if (isAnalyzing.value && newContent && newContent.length > (oldContent?.length || 0)) {
      scrollToBottom();
    }
  },
  { flush: 'post' },
);

// 获取错误标题
const getErrorTitle = () => {
  if (!props.error) {
    return '分析失败: connection';
  }

  if (props.error.includes('connection') || props.error.includes('网络')) {
    return '连接失败';
  }
  if (props.error.includes('timeout') || props.error.includes('超时')) {
    return '分析超时';
  }
  if (props.error.includes('quota') || props.error.includes('配额')) {
    return '服务配额不足';
  }
  if (props.error.includes('parsing') || props.error.includes('解析')) {
    return '结果解析失败';
  }
  return '分析失败';
};
</script>

<style scoped>
/* .analysis-status-card height controlled by flex layout */

.thinking-preview {
  border-radius: 0.5rem;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
}

.thinking-preview::-webkit-scrollbar {
  width: 6px;
}

.thinking-preview::-webkit-scrollbar-track {
  background-color: #f3f4f6;
  border-radius: 0.25rem;
}

.thinking-preview::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 0.25rem;
}

.thinking-preview::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}

.empty-state {
  color: #9ca3af;
}

.error-state {
  text-align: center;
}

.thinking-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* 动画效果 */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes ping {
  75%,
  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.animate-ping {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.scroll-to-bottom-btn {
  display: inline-flex;
  cursor: pointer;
  align-items: center;
  gap: 0.5rem;
  border-radius: 9999px;
  background-color: #3b82f6;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  color: white;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: background-color 0.2s;
}

.scroll-to-bottom-btn:hover {
  background-color: #2563eb;
}
</style>
